import type { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import TradingDatagridV3 from '@trading-ui/datagrid-v3';
import { useEffect, useRef } from 'react';
import { useResizeObserver } from 'usehooks-ts';

import TableLoader from '@/components/table-loader/TableLoader';

import type { ConsumptionChartResponse, ConsumptionTableRow, Plant } from '@/interfaces';
import type { PricingIndex } from '@/api/services/settings/pricing-indices.service';

import { useConsumptionTable } from './hooks/useConsumptionTable';

import * as styles from './ConsumptionTable.css';

type Props = {
  processedData: ConsumptionTableRow[];
  isLoading: boolean;
  mutateChart: (options?: RefetchOptions) => Promise<QueryObserverResult<ConsumptionChartResponse, Error>>;
  pricingIndices: PricingIndex[];
  plantPricingIndexMap: Map<number, number | null>;
  duplicateIdentifiers: Set<string>;
  duplicatePlants: Array<Plant & { companyName?: string }>;
};

export default function ConsumptionTable({
  processedData,
  isLoading,
  mutateChart,
  pricingIndices,
  plantPricingIndexMap,
  duplicateIdentifiers,
  duplicatePlants,
}: Props) {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  // @ts-ignore
  const { height = 0 } = useResizeObserver({ ref: tableContainerRef });
  const { theme, columns, options, tableInstance, getTableInstance } = useConsumptionTable({
    mutateChart,
    height,
    pricingIndices,
    plantPricingIndexMap,
    duplicateIdentifiers,
    duplicatePlants,
  });

  useEffect(() => {
    if (tableInstance && processedData.length > 0) {
      tableInstance.setData(processedData as any);
    }
  }, [processedData, tableInstance]);

  return (
    <div className={styles.root} ref={tableContainerRef}>
      {isLoading && <TableLoader />}
      {columns.length > 0 && height > 0 && (
        <TradingDatagridV3
          customId="consumption-table"
          getTableInstance={getTableInstance}
          columns={columns}
          theme={theme}
          data={[]}
          options={options}
        />
      )}
    </div>
  );
}
